import { useEffect, useState } from 'react';
import {
  normalizeMarkdownCallouts,
  stripMarkdownBackLink,
} from '../../../Markdown';
import { UseRemoteMarkdownDocResult } from './types';

export function useRemoteMarkdownDoc(
  sourceUrl: string | null,
): UseRemoteMarkdownDocResult {
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!sourceUrl) {
      setMarkdown(null);
      setIsLoading(false);
      setHasError(false);
      return undefined;
    }
    const markdownSourceUrl = sourceUrl;

    const abortController = new AbortController();
    let isDisposed = false;

    async function loadMarkdownDoc() {
      setIsLoading(true);
      setHasError(false);
      setMarkdown(null);

      try {
        const response = await fetch(markdownSourceUrl, {
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to load markdown from ${markdownSourceUrl}`);
        }

        const text = await response.text();

        if (isDisposed) {
          return;
        }

        const normalizedMarkdown = normalizeMarkdownCallouts(
          stripMarkdownBackLink(text),
        );

        setMarkdown(normalizedMarkdown);
      } catch (_error) {
        if (abortController.signal.aborted || isDisposed) {
          return;
        }

        setHasError(true);
      } finally {
        if (!abortController.signal.aborted && !isDisposed) {
          setIsLoading(false);
        }
      }
    }

    loadMarkdownDoc();

    return () => {
      isDisposed = true;
      abortController.abort();
    };
  }, [sourceUrl]);

  return {
    markdown,
    isLoading,
    hasError,
  };
}
